import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@heroui/react';
import { FiTrash2 } from 'react-icons/fi';
import { UserAvatar } from '~/components/atoms/UserAvatar';
import { BaseLink } from '~/components/atoms/Link';
import { useLocale } from '~/hooks/useLocale';
import { dateLocales } from '~/locales';
import { ROUTES } from '~/constants/routes.contants';
import { ReviewCommentResponseDto } from '@bitecritic-api/src/review-comments/dto/review-comment.response.dto';
import useUserContext from '~/hooks/useUserContext';

interface CommentItemProps {
  comment: ReviewCommentResponseDto;
  onDelete?: (commentId: string) => void;
  onNavigateToUser: () => void;
}

export const CommentItem: React.FC<CommentItemProps> = ({ comment, onDelete, onNavigateToUser }) => {
  const { currentLocale, navigateTo } = useLocale();
  const { user } = useUserContext();
  
  const isOwner = user?.id === comment.user.id;
  
  const handleUserClick = (messageUsername?: string) => {
    onNavigateToUser();
    navigateTo(`${ROUTES.USERS}/${messageUsername || comment.user.username}`);
  };
  
  const handleDelete = () => {
    if (onDelete) {
      onDelete(comment.id);
    }
  };
  
  // Ensure we have a proper Date object
  const commentDate = comment.createdAt instanceof Date 
    ? comment.createdAt 
    : new Date(comment.createdAt);
  
  // Parse text with mentions (@[username](userId))
  const renderCommentText = () => {
    if (!comment.text) return '';
    
    // Regular expression to match mention format: @[username](userId)
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    
    // Split text by mentions
    const parts = comment.text.split(mentionRegex);
    
    if (parts.length === 1) {
      // No mentions found
      return <p className="text-sm mt-2 text-foreground-700">{comment.text}</p>;
    }
    
    // Extract mentions from the original text
    const mentions: { username: string; userId: string; fullMatch: string }[] = [];
    let match;
    while ((match = mentionRegex.exec(comment.text)) !== null) {
      mentions.push({
        fullMatch: match[0],
        username: match[1],
        userId: match[2]
      });
    }
    
    // Build the rendered text with mention links
    const result: React.ReactNode[] = [];
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (i % 3 === 0) {
        // Regular text
        if (part) {
          result.push(<span key={`text-${i}`}>{part}</span>);
        }
      } else if (i % 3 === 1) {
        // Username
        result.push(
          <BaseLink
            key={`mention-${i}`}
            variant="secondary"
            href={`${ROUTES.USERS}/${part}`}
            className="font-semibold text-secondary-600 hover:underline text-sm"
            onClick={() => handleUserClick(part)}
          >
            @{part}
          </BaseLink>
        );
        i++; // Skip the userId part in the next iteration
      }
    }
    
    return <p className="text-sm mt-2 text-foreground-700">{result}</p>;
  };
  
  return (
    <div className="flex gap-3 py-3 border-b border-secondary-100 last:border-0">
      <UserAvatar
        user={comment.user}
        size="md"
        className="cursor-pointer mt-1 flex-shrink-0"
        onClick={handleUserClick}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col min-w-0">
            <span className="text-xs text-secondary-500 mb-1">
              {formatDistanceToNow(commentDate, {
                addSuffix: true,
                locale: dateLocales[currentLocale.language as keyof typeof dateLocales]
              })}
            </span>
            <BaseLink
              variant="secondary"
              href={`${ROUTES.USERS}/${comment.user.username}`}
              onClick={() => {
                handleUserClick(comment.user.username);
              }}
              className="font-semibold text-sm text-foreground-800 hover:text-warning-600 line-clamp-2 overflow-hidden text-ellipsis"
            >
              {comment.user.username}
            </BaseLink>
          </div>
          {isOwner && (
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              aria-label="Delete comment"
              className="flex-shrink-0"
              onPress={handleDelete}
            >
              <FiTrash2 size={16} />
            </Button>
          )}
        </div>
        {renderCommentText()}
      </div>
    </div>
  );
}; 